// Mock data for timetables - SAT V4 Zone
import type { Timetable, TimetableEntry, Conflict, TimetableVersion, SimulationResult, DayOfWeek } from '../../types';
import { mockTeachers } from './teachers';
import { mockRooms } from './rooms';
import { mockSubjects } from './subjects';
import { mockClasses } from './classes';

const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
// Lesson period indices (order values) matching periods.ts structure
const lessonPeriodOrders = [1, 2, 4, 5, 7, 8]; // Period 1-6 (excluding breaks/registration)

// Generate sample timetable entries - ensures all day/period slots have data
function generateEntries(timetableId: string): TimetableEntry[] {
  const entries: TimetableEntry[] = [];
  let entryIndex = 0;

  // Create entries for each class across all days and periods
  for (const classItem of mockClasses.slice(0, 15)) { // First 15 classes for cleaner display
    for (const day of days) {
      for (const periodOrder of lessonPeriodOrders) {
        const subject = mockSubjects[entryIndex % mockSubjects.length];
        const teacher = mockTeachers.find(t => t.subjects.includes(subject.code)) || mockTeachers[entryIndex % mockTeachers.length];
        const room = mockRooms[entryIndex % mockRooms.length];

        // Assign week A or B based on entry index for bi-weekly timetables
        // Some entries are Week A only, some Week B only, some appear in both (no week assignment)
        const weekAssignment: 'A' | 'B' | undefined = entryIndex % 3 === 0 ? 'A' : entryIndex % 3 === 1 ? 'B' : undefined;

        entries.push({
          id: `${timetableId}-entry-${entryIndex}`,
          dayOfWeek: day,
          periodId: `${day}-p${periodOrder}`, // Format: monday-p1, tuesday-p2, etc.
          subjectId: subject.id,
          classId: classItem.id,
          teacherId: teacher.id,
          roomId: room.id,
          isLocked: entryIndex % 20 === 0, // 5% locked entries (deterministic)
          notes: entryIndex % 50 === 0 ? 'Special arrangement' : undefined,
          week: weekAssignment,
        });

        entryIndex++;
      }
    }
  }

  return entries;
}

// Generate some conflicts
function generateConflicts(): Conflict[] {
  return [
    {
      id: 'conflict-001',
      type: 'teacher_double_booking',
      severity: 'error',
      description: 'Mr Smith is scheduled for two lessons at Monday Period 3',
      affectedEntries: ['tt-main-entry-15', 'tt-main-entry-42'],
      suggestedResolutions: ['Move English 7A to Period 4', 'Assign different teacher'],
      status: 'unresolved',
    },
    {
      id: 'conflict-002',
      type: 'room_double_booking',
      severity: 'error',
      description: 'Science Lab 1 is double-booked on Tuesday Period 2',
      affectedEntries: ['tt-main-entry-28', 'tt-main-entry-55'],
      suggestedResolutions: ['Move Chemistry 10A to Lab 2', 'Reschedule to Period 5'],
      status: 'unresolved',
    },
    {
      id: 'conflict-003',
      type: 'capacity_exceeded',
      severity: 'warning',
      description: 'Room MB03 has 32 pupils but capacity is 30',
      affectedEntries: ['tt-main-entry-88'],
      suggestedResolutions: ['Move to larger room MB05', 'Split class into two groups'],
      status: 'unresolved',
    },
    {
      id: 'conflict-004',
      type: 'rule_violation',
      severity: 'warning',
      description: 'Soft rule violation: PE scheduled in last period on Friday',
      affectedEntries: ['tt-main-entry-120'],
      suggestedResolutions: ['Move PE to earlier slot', 'Accept violation'],
      status: 'ignored',
    },
  ];
}

export const mockTimetables: Timetable[] = [
  {
    id: 'tt-main',
    name: 'Academic Year 2024-25',
    version: 3,
    academicYear: '2024-2025',
    term: 'autumn',
    status: 'finalized',
    createdAt: '2024-08-15T10:00:00Z',
    updatedAt: '2024-09-01T14:30:00Z',
    createdBy: 'admin',
    entries: generateEntries('tt-main'),
    conflicts: generateConflicts(),
    metrics: {
      totalLessons: 1250,
      conflictCount: 4,
      hardRulesSatisfied: 7,
      hardRulesTotal: 7,
      softRulesSatisfied: 4,
      softRulesTotal: 6,
      roomUtilization: 78,
      teacherUtilization: 85,
    },
  },
  {
    id: 'tt-draft',
    name: 'Spring Term 2024-25 (Draft)',
    version: 1,
    academicYear: '2024-2025',
    term: 'spring',
    status: 'draft',
    createdAt: '2024-11-20T09:00:00Z',
    updatedAt: '2024-11-20T09:00:00Z',
    createdBy: 'admin',
    entries: generateEntries('tt-draft'),
    conflicts: [],
    metrics: {
      totalLessons: 1200,
      conflictCount: 0,
      hardRulesSatisfied: 7,
      hardRulesTotal: 7,
      softRulesSatisfied: 5,
      softRulesTotal: 6,
      roomUtilization: 75,
      teacherUtilization: 82,
    },
  },
];

export const mockTimetableVersions: TimetableVersion[] = [
  {
    id: 'ttv-001',
    timetableId: 'tt-main',
    version: 1,
    name: 'Initial Generation',
    createdAt: '2024-08-15T10:00:00Z',
    createdBy: 'admin',
    changesSummary: 'First automated generation',
    canRestore: true,
  },
  {
    id: 'ttv-002',
    timetableId: 'tt-main',
    version: 2,
    name: 'After Manual Adjustments',
    createdAt: '2024-08-20T15:00:00Z',
    createdBy: 'admin',
    changesSummary: 'Resolved 8 conflicts, adjusted PE schedule',
    canRestore: true,
  },
  {
    id: 'ttv-003',
    timetableId: 'tt-main',
    version: 3,
    name: 'Final Approved',
    createdAt: '2024-09-01T14:30:00Z',
    createdBy: 'admin',
    changesSummary: 'Final approval after staff review',
    canRestore: false,
  },
];

export const mockSimulationResults: SimulationResult[] = [
  {
    id: 'sim-001',
    name: 'Balanced Approach',
    parameters: { profile: 'profile-001' },
    metrics: {
      totalLessons: 1250,
      conflictCount: 4,
      hardRulesSatisfied: 7,
      hardRulesTotal: 7,
      softRulesSatisfied: 4,
      softRulesTotal: 6,
      roomUtilization: 78,
      teacherUtilization: 85,
    },
    createdAt: '2024-08-14T09:00:00Z',
    status: 'completed',
  },
  {
    id: 'sim-002',
    name: 'Teacher Focused',
    parameters: { profile: 'profile-002' },
    metrics: {
      totalLessons: 1250,
      conflictCount: 6,
      hardRulesSatisfied: 7,
      hardRulesTotal: 7,
      softRulesSatisfied: 5,
      softRulesTotal: 6,
      roomUtilization: 72,
      teacherUtilization: 92,
    },
    createdAt: '2024-08-14T09:30:00Z',
    status: 'completed',
  },
  {
    id: 'sim-003',
    name: 'Room Optimized',
    parameters: { profile: 'profile-003' },
    metrics: {
      totalLessons: 1250,
      conflictCount: 3,
      hardRulesSatisfied: 7,
      hardRulesTotal: 7,
      softRulesSatisfied: 3,
      softRulesTotal: 6,
      roomUtilization: 88,
      teacherUtilization: 80,
    },
    createdAt: '2024-08-14T10:00:00Z',
    status: 'completed',
  },
];

export default mockTimetables;

